import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import type { OfferPageFilterType, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import GeoContext from 'contexts/geoContext'
import moment from 'moment'
import getBedbankPromoExtraValue, { getDisplayTotalPricePerNight, getDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import { useAppDispatch } from 'hooks/reduxHooks'
import { setCustomerValue } from 'actions/AuthActions'
import useBedbankDiscount from 'hooks/useBedbankDiscount'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import { generateLoyaltyPointsCalculatorBedbankOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import * as Analytics from 'analytics/analytics'
import { useQsQueryParams } from 'hooks/useQueryParams'
import { shouldShowPayAtPropertyPill, shouldShowPayLaterPill } from '../../utils'
import { arrayToObject } from 'lib/array/arrayUtils'
import BedbankSummaryPriceDetailsView from './BedbankSummaryPriceDetailsView'

export interface RoomWithSummaryData {
  room: OfferPageRoom
  hotelOnly: {
    package: App.BedbankPackage
    rate?: App.BedbankRate
    sell?: App.BedbankSellPriceInfo
    promotion?: App.BedbankPromotion
    capacityValid: boolean
  }
  bundle: {
    package: App.BedbankPackage
    rate?: App.BedbankRate
    sell?: App.BedbankSellPriceInfo
    promotion?: App.BedbankPromotion
    capacityValid: boolean
  }
}

interface Props {
  roomsWithData: Array<RoomWithSummaryData>
  /**
   * rate here is lowest rate within the offer
   */
  rooms: Array<App.Occupants>
  offer: App.BedbankOffer
  flightOrigin?: App.Airport
  flightPrice?: number
  duration: number
  checkIn?: string
  departureTooEarly?: boolean
  flightsEnabled: boolean
  onViewPackageOptions?: () => void
  promotion?: boolean
  alignment?: 'center' | 'start'
  luxPlusLPPEnabled: boolean
  priceTrackedRef?: React.RefObject<boolean>
  isFlightsSelected?: boolean
}

function BedbankSummaryPriceDetailsIndividualRooms(props: Props) {
  const {
    roomsWithData,
    rooms,
    offer,
    checkIn,
    flightOrigin,
    flightPrice,
    departureTooEarly,
    flightsEnabled,
    duration,
    onViewPackageOptions,
    promotion = false,
    alignment = 'center',
    luxPlusLPPEnabled,
    priceTrackedRef,
    isFlightsSelected,
  } = props

  const { currentRegionCode } = useContext(GeoContext)
  const offerPageDispatch = useOfferPageDispatch()
  const appDispatch = useAppDispatch()
  const hasFlights = !!flightPrice

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterType })
  }, [offerPageDispatch])

  const {
    propertyFees,
    hotelPrice,
    hotelMemberPrice,
    hotelValue,
    bundlePropertyFees,
    bundlePrice,
    bundleMemberPrice,
    bundleValue,
    taxesAndFees,
    bundleTaxesAndFees,
  } = useMemo(() => {
    return roomsWithData.reduce((acc, roomData) => {
      const rate = roomData.hotelOnly.rate
      const sell = roomData.hotelOnly.package.sell
      const bundleRate = roomData.bundle.rate
      const bundleSell = roomData.bundle.package.sell

      const hotelPrice = rate?.totals.inclusive ?? sell?.price ?? 0
      let hotelValue = rate?.value ?? sell?.value ?? 0
      const isPromoEnabledRate = !!roomData.hotelOnly.promotion
      if (isPromoEnabledRate && sell) {
        const inclusionsAmount = getBedbankPromoExtraValue({
          price: sell.price,
          value: sell.value,
          minLos: sell.los,
          duration,
        })
        hotelValue = inclusionsAmount + hotelPrice
      }

      const bundlePrice = bundleRate?.totals.inclusive ?? bundleSell?.price ?? 0
      let bundleValue = bundleRate?.value ?? bundleSell?.value ?? 0
      const bundleIsPromoEnabledRate = !!roomData.bundle.promotion
      if (bundleIsPromoEnabledRate && bundleSell) {
        const bundleInclusionsAmount = getBedbankPromoExtraValue({
          price: bundleSell.price,
          value: bundleSell.value,
          minLos: bundleSell.los,
          duration,
        })
        bundleValue = bundleInclusionsAmount + bundlePrice
      }

      return {
        propertyFees: acc.propertyFees + (rate?.totals.propertyFees ?? sell?.propertyFees ?? 0),
        taxesAndFees: acc.taxesAndFees + (rate?.totals.taxesAndFees ?? sell?.taxesAndFees ?? 0),
        hotelPrice: acc.hotelPrice + hotelPrice,
        hotelMemberPrice: acc.hotelMemberPrice + (rate?.totals.memberInclusive ?? sell?.price ?? 0),
        margin: acc.margin + (rate?.margin || 0),
        marginAud: acc.marginAud + (rate?.marginAud || 0),
        hotelValue: acc.hotelValue + hotelValue,

        bundlePropertyFees: acc.bundlePropertyFees + (bundleRate?.totals.propertyFees ?? bundleSell?.propertyFees ?? 0),
        bundleTaxesAndFees: acc.bundleTaxesAndFees + (bundleRate?.totals.taxesAndFees ?? bundleSell?.taxesAndFees ?? 0),
        bundlePrice: acc.bundlePrice + bundlePrice,
        bundleMemberPrice: acc.bundleMemberPrice + (bundleRate?.totals.memberInclusive ?? bundleSell?.price ?? 0),
        bundleMargin: acc.bundleMargin + (bundleRate?.margin || 0),
        bundleMarginAud: acc.bundleMarginAud + (bundleRate?.marginAud || 0),
        bundleValue: acc.bundleValue + bundleValue,
      }
    }, {
      propertyFees: 0,
      taxesAndFees: 0,
      hotelPrice: 0,
      hotelMemberPrice: 0,
      margin: 0,
      marginAud: 0,
      hotelValue: 0,
      bundlePropertyFees: 0,
      bundleTaxesAndFees: 0,
      bundlePrice: 0,
      bundleMemberPrice: 0,
      bundleMargin: 0,
      bundleMarginAud: 0,
      bundleValue: 0,
    })
  }, [duration, roomsWithData])

  const totalsByRoom = useMemo(() => arrayToObject(
    roomsWithData,
    roomData => roomData.room.id,
    roomData => {
      const rate = roomData.hotelOnly.rate
      const sell = roomData.hotelOnly.sell
      const bundleRate = roomData.bundle.rate
      const bundleSell = roomData.bundle.sell
      const propertyFees = rate?.totals.propertyFees ?? sell?.propertyFees ?? 0
      const bundlePropertyFees = bundleRate?.totals.propertyFees || propertyFees

      const hotelPrice = rate?.totals.inclusive ?? sell?.price ?? 0
      const hotelMemberPrice = rate?.totals.memberInclusive ?? sell?.price ?? 0
      const bundlePrice = bundleRate?.totals.inclusive || hotelPrice

      return getDisplayTotalPrice({
        hotelPrice: hotelPrice + propertyFees,
        hotelMemberPrice: hotelMemberPrice > 0 ? hotelMemberPrice + propertyFees : 0,
        hotelValue: rate?.value ?? sell?.value,
        bundlePrice: bundlePrice + bundlePropertyFees,
        bundleValue: bundleRate?.value ?? bundleSell?.value,
        checkIn,
        flightPrice: flightPrice ? flightPrice / roomsWithData.length : undefined,
        rooms: [roomData.room],
      })
    },
  ), [checkIn, flightPrice, roomsWithData])

  const departureTooFarInFuture = useMemo(() => checkIn ? moment(checkIn).diff(moment.utc(), 'days') > 330 : false, [checkIn])

  const saleUnit = offer.displayPricingAsPerPerson ? 'person' : 'room'
  const promoteAsBundle = flightsEnabled && !!flightPrice && offer.promoteAsBundle

  const baseTotalPrices = getDisplayTotalPrice({
    hotelPrice: hotelPrice + propertyFees,
    hotelMemberPrice: hotelMemberPrice > 0 ? hotelMemberPrice + propertyFees : 0,
    hotelValue,
    bundlePrice: bundlePrice + bundlePropertyFees,
    bundleValue,
    checkIn,
    flightPrice,
    rooms,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
  })
  const totalPricesPerNight = getDisplayTotalPricePerNight(baseTotalPrices, duration || 1)
  const showOneNightPrice = !promotion && !offer.displayPricingAsPerPerson && !checkIn && !!duration && duration > 1
  const totalPrices = showOneNightPrice ? totalPricesPerNight : baseTotalPrices
  const nightsToDisplay = showOneNightPrice ? 1 : duration

  const offerLocations: Array<string> = useMemo(() => {
    const locations = [
      offer.property?.address.city,
      offer.property?.address.stateProvinceName || '',
      offer.property?.address.countryName]
      .filter(Boolean)

    return locations
  }, [offer])

  const discount = promoteAsBundle ? totalPrices.original.discountWithFlight : totalPrices.original.discount
  const memberDiscount = promoteAsBundle ? totalPrices.original.memberDiscountWithFlight : totalPrices.original.memberDiscount
  const displayDiscount = useBedbankDiscount(discount, memberDiscount)

  useEffect(() => {
    appDispatch(setCustomerValue(totalPrices.totals.price))
  }, [appDispatch, totalPrices.totals.price])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return [
      ...roomsWithData.map(roomData => {
        const rate = roomData.hotelOnly.rate
        const roomTotalPrices = totalsByRoom[roomData.room.id]

        return (
          generateLoyaltyPointsCalculatorBedbankOptions(rate, {
            price: roomTotalPrices.totals.price,
            memberPrice: roomTotalPrices.totals.memberPrice,
            propertyFees: rate?.totals.propertyFees ?? 0,
            propertyId: offer.property.id,
            offerId: offer.id,
            propertyCountryCode: offer.property.address.countryCode,
            currentRegionCode,
          })
        )
      }),
    ]
  }, [
    totalsByRoom,
    roomsWithData,
    offer,
    currentRegionCode,
  ])

  const showHotelPriceWithFlights = !!flightsEnabled && hasFlights && !offer.promoteAsBundle
  const priceToTrack = showHotelPriceWithFlights ?
    totalPrices?.totals?.price :
    promoteAsBundle ? totalPrices?.totals?.flightBundlePrice : totalPrices?.totals?.price

  const shouldTrackPrice = !!(showHotelPriceWithFlights ? totalPrices?.totals?.price : totalPrices?.totals?.flightBundlePrice || totalPrices?.totals?.price)
  const { searchId } = useQsQueryParams<{ searchId?: string }>()

  useEffect(() => {
    if (shouldTrackPrice && !!priceToTrack && searchId && offer?.id && priceTrackedRef && !priceTrackedRef.current) {
      Analytics.trackClientEvent({
        subject: `searchId:${searchId}_offerId:${offer.id}`,
        action: priceToTrack.toString(),
        category: 'price_accuracy',
        type: 'operational',
      })
      priceTrackedRef.current = true
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldTrackPrice, priceToTrack, offer?.id])

  const canPayLater = roomsWithData.length === 1 && !!roomsWithData[0].hotelOnly.rate && shouldShowPayLaterPill(roomsWithData[0].hotelOnly.rate)
  const showPayAtPropertyPill = !isFlightsSelected && roomsWithData.length === 1 && !!roomsWithData[0].hotelOnly.rate && shouldShowPayAtPropertyPill(roomsWithData[0].hotelOnly.rate)

  return <BedbankSummaryPriceDetailsView
    rooms={rooms}
    offer={offer}
    flightOrigin={flightOrigin}
    flightPrice={flightPrice}
    duration={duration}
    departureTooEarly={departureTooEarly}
    flightsEnabled={flightsEnabled}
    onViewPackageOptions={onViewPackageOptions}
    onToggleFilter={onToggleFilter}
    alignment={alignment}
    luxPlusLPPEnabled={luxPlusLPPEnabled}
    showPayAtPropertyPill={showPayAtPropertyPill}
    showHotelPriceWithFlights={showHotelPriceWithFlights}
    canPayLater={canPayLater}
    displayDiscount={displayDiscount}
    nightsToDisplay={nightsToDisplay}
    totalPrices={totalPrices}
    saleUnit={saleUnit}
    taxesAndFees={taxesAndFees}
    propertyFees={propertyFees}
    hotelPrice={hotelPrice}
    hotelMemberPrice={hotelMemberPrice}
    offerLocations={offerLocations}
    hotelValue={hotelValue}
    bundlePropertyFees={bundlePropertyFees}
    bundlePrice={bundlePrice}
    bundleMemberPrice={bundleMemberPrice}
    bundleTaxesAndFees={bundleTaxesAndFees}
    pointsEarnCalculationRequests={pointsEarnCalculationRequests}
    hasFlights={hasFlights}
    departureTooFarInFuture={departureTooFarInFuture}
    promoteAsBundle={promoteAsBundle}
  />
}

export default withOfferPageAndReduxState((state, appState) => ({
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
  luxPlusLPPEnabled: getIsLuxPlusLPPEnabled(appState),
  isFlightsSelected: state.flights,
}), BedbankSummaryPriceDetailsIndividualRooms)
